import React, { useCallback, useEffect, useRef, useState } from 'react';
import { actions as CanvasActions } from '~app/features/canvas';
import { useDispatch, useSelector } from 'react-redux';
/*
 * A label that can be editable. The label will display as a plain div until the
 * component recieves focus, and then it will turn into a text input field.
 * Changes are only sent after editing is completed by pressing Enter or
 * changing focus. The value is semi-controlled: if the input value changes
 * by something other than this component, the internal value will be changed
 * to the input value (overwriting any local changes). Otherwise, the internal
 * value is updated as the user enters text.
 *
 * Empty values are not allowed; if the user deletes the contents and hits
 * enter, the original value will be restored.
 *
 * The input is trimmed of whitespace before sending to onFinalChange.
 */

function EditableLabel({
  isEditable,
  className,
  value,
  onFinalChange,
  maxLength,
  labelFocus,
}) {
  const inputRef = useRef();
  const [inputText, setInputText] = useState(value);
  const [savedValue, setSavedValue] = useState(value);
  const [focusedDiv, setFocusedDiv] = useState(false);

  const dispatch = useDispatch();

  const duplicateName = useSelector(
    state => state.document.present?.duplicateName,
  );

  useEffect(() => {
    if (duplicateName) {
      setInputText(savedValue);
    }
  }, [duplicateName, savedValue]);

  useEffect(() => {
    if (value !== savedValue) {
      setSavedValue(value);
      setInputText(value);
    }
  }, [savedValue, value]);

  useEffect(() => {
    if (!isEditable) {
      setFocusedDiv(false);
      dispatch(CanvasActions.labelFocusOff());
    }
  }, [dispatch, isEditable]);

  const onChange = useCallback(e => {
    setInputText(e.target.value);
  }, []);

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        const v = inputText.trim();
        if (v === '') {
          setInputText(savedValue);
        } else if (v !== savedValue) {
          onFinalChange(v);
        }
      } else if (e.key === 'Escape') {
        setInputText(savedValue);
      } else {
        return;
      }
      // Need to wait a tick to blur, otherwise onBlur() will execute during the
      // same update and overwrite the above setInputText or generate an unwanted
      // onFinalChange() call.
      // XXX this could lead to a race where inputRef.current is no longer valid
      // due to, e.g., the component getting unmounted at the same time. Mitigate
      // that a bit by capturing inputRef.current first.
      const current = inputRef.current;
      setTimeout(() => current.blur(), 0);
    },
    [onFinalChange, inputText, savedValue, inputRef],
  );

  const onFocusDiv = useCallback(() => {
    setFocusedDiv(true);
    dispatch(CanvasActions.labelFocusOn());
  }, [dispatch]);

  const onFocusInput = useCallback(() => {
    setFocusedDiv(true);
    dispatch(CanvasActions.labelFocusOn());
  }, [dispatch]);

  const onBlur = useCallback(() => {
    setFocusedDiv(false);
    dispatch(CanvasActions.labelFocusOff());
    const v = inputText.trim();
    if (v === '') {
      setInputText(savedValue);
    } else if (v !== savedValue) {
      onFinalChange(v);
    }
  }, [onFinalChange, inputText, savedValue, dispatch]);

  const onMouse = useCallback(
    e => {
      if (focusedDiv === true && isEditable) {
        // If we're editing, prevent mouse events from propagating outward, so that
        // draggable's handlers, etc, don't disrupt clicks/drags from the user editing
        // the text.
        e.stopPropagation();
      }
    },
    [focusedDiv, isEditable],
  );

  return (
    <div
      className={className}
      tabIndex={isEditable ? 0 : undefined}
      onFocus={onFocusDiv}
      onTouchStart={onFocusDiv}
      onClick={onMouse}
      onDrag={onMouse}
      onMouseDown={onMouse}
    >
      {!isEditable || !focusedDiv ? (
        value
      ) : (
        <input
          className="canvas-object"
          ref={inputRef}
          value={inputText}
          onFocus={onFocusInput}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          maxLength={maxLength}
        />
      )}
    </div>
  );
}

export default EditableLabel;
